import { Link } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import { useState } from 'react';
import { Card } from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import type { AccountChangeEntry, ChangeLevel } from '../testrun-types';
import { useAccountChangesLog } from './api';

/**
 * The account data watch: what changed for the pool's accounts between checks
 * (TestRun server/src/watch.mjs). Every night the watch checks the pool by
 * itself, read only — in the morning this shows who got an employee account,
 * whose loan became overdue, which account stopped fitting. It only shows;
 * repairs are the account's buttons.
 */

const LEVEL_DOT: Record<ChangeLevel, string> = {
  bad: 'bg-fail',
  good: 'bg-pass',
  info: 'bg-ink-subtle',
};

/** How TestRun names the night pass in `by`. */
const WATCH_BY = 'сторож';
const PERIODS = [1, 7, 30] as const;

/** `poolId` — one account's history (on its page), otherwise the whole pool; `live` — «check all» runs. */
export const AccountChanges = ({ poolId, live = false }: { poolId?: string; live?: boolean }) => {
  const { t } = useT();
  const { relative, dateTime } = useFormat();
  const [days, setDays] = useState<number>(poolId ? 30 : 1);
  const { data } = useAccountChangesLog(days, poolId, live);
  // the log is extra information: while it loads or if it fails, the page goes on without it
  if (!data) return null;

  const { watch, entries } = data;
  const total = entries.reduce((sum, e) => sum + e.changes.length, 0);
  const bad = entries.reduce(
    (sum, e) => sum + e.changes.filter((c) => c.level === 'bad').length,
    0,
  );
  const levelTitle = (level: ChangeLevel) => t(`automation.accounts.changes.level.${level}`);
  const accountName = (e: AccountChangeEntry) =>
    e.account.label || e.account.companyName || t('automation.accounts.changes.account');

  return (
    <Card>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="flex items-center gap-2 text-[14px] font-semibold text-ink">
          <Eye className="size-4" aria-hidden="true" />
          {poolId
            ? t('automation.accounts.changes.history')
            : t('automation.accounts.changes.title')}
          {total > 0 ? (
            <span className="font-normal text-ink-muted">
              · {total}
              {bad ? `, ${t('automation.accounts.changes.worse', { count: bad })}` : ''}
            </span>
          ) : null}
        </h2>
        {!poolId ? (
          <div
            role="group"
            aria-label={t('automation.accounts.changes.period')}
            className="flex gap-0.5 rounded-md bg-sunken p-0.5"
          >
            {PERIODS.map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={days === d}
                onClick={() => setDays(d)}
                className={cn(
                  'rounded px-2.5 py-1 text-[12px] font-medium transition-colors',
                  days === d ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
                )}
              >
                {t('automation.accounts.changes.days', { count: d })}
              </button>
            ))}
          </div>
        ) : null}
      </header>
      <div className="space-y-3 px-4 py-3">
        <p className="text-[12px] text-ink-muted">
          {watch.enabled
            ? t('automation.accounts.changes.watchOn', {
                at: watch.at ?? '',
                tz: watch.tz,
                last: watch.lastRunAt
                  ? relative(watch.lastRunAt)
                  : t('automation.accounts.changes.never'),
                next: watch.next ?? t('common.empty'),
              })
            : t('automation.accounts.changes.watchOff')}
        </p>

        {!entries.length ? (
          <p className="text-[13px] text-ink-muted">
            {t('automation.accounts.changes.none', { count: days })}
          </p>
        ) : (
          <ul className="space-y-2">
            {entries.map((e) => (
              <li key={e.id} className="rounded-lg border border-border px-3 py-2">
                <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                  {poolId ? (
                    <span className="text-[13px] font-semibold text-ink">{dateTime(e.at)}</span>
                  ) : (
                    <Link
                      to="/automation/accounts/$accountId"
                      params={{ accountId: e.poolId }}
                      className="text-[13px] font-semibold text-ink hover:text-accent-fg hover:underline"
                    >
                      {accountName(e)}
                      {e.account.type ? ` · ${e.account.type}` : ''}
                    </Link>
                  )}
                  <span className="text-[12px] text-ink-subtle">
                    {poolId ? '' : `${relative(e.at)} · `}
                    {e.by === WATCH_BY
                      ? t('automation.accounts.changes.byWatch')
                      : t('automation.accounts.changes.byUser', { user: e.by })}
                  </span>
                </div>
                <ul className="space-y-1">
                  {e.changes.map((c, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[13px]"
                      title={levelTitle(c.level)}
                    >
                      <span
                        className={cn('mt-1.5 size-2 shrink-0 rounded-full', LEVEL_DOT[c.level])}
                        aria-hidden="true"
                      />
                      <span className="sr-only">{levelTitle(c.level)}: </span>
                      <span
                        className={cn(
                          'break-words',
                          c.level === 'info' ? 'text-ink-muted' : 'text-ink',
                        )}
                      >
                        {c.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};
